import { hasOtherLeader, initialsOf, leadsGroup } from '@echo/core';
import { useGroupPeople, useMe, useMyGroup, useRemoveGroupMember } from '@echo/core/react';
import { Avatar, Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed, GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { GroupRoleTag } from '@/components/group-role-tag';
import { InfoIcon } from '@/components/info-icon';
import { useAuthorizedImageUrl } from '@/lib/authorized-image';

export const Route = createFileRoute('/_app/account_/groups_/$id_/member/$personId_/remove')({
  component: RemoveFromGroupPage,
});

/**
 * Member profile › Remove from group (Figma member, "Remove from group"): who leaves and what it
 * means, then "Remove from group" back to People and access without them, or Cancel. For you it
 * is leaving the group, which the last Group Leader or Facilitator cannot do until someone else
 * leads it. A member who doesn't lead the group only ever sees this for themselves, from Leave
 * group: Core shows them no one else.
 */
function RemoveFromGroupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const me = useMe();
  const photo = useAuthorizedImageUrl(me.data?.profileImage?.medium?.url ?? undefined);
  const { id, personId } = Route.useParams();
  const { group, ...groups } = useMyGroup(id);
  const leads = group?.manages ?? false;
  const people = useGroupPeople(id, leads);
  const removing = useRemoveGroupMember(id);
  const yours = personId === me.data?.uuid;
  // Leave group comes from Manage group, Remove from group from the member profile: back to
  // whichever it was. Opened from a link there is nothing behind it: fall back to that page.
  const back = () => {
    if (canGoBack) return router.history.back();
    if (yours) return void navigate({ to: '/account/groups/$id', params: { id } });
    void navigate({ to: '/account/groups/$id/member/$personId', params: { id, personId } });
  };
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={back}>
          {children}
        </button>
      )}
    />
  );
  const heading = (
    <h1 className="text-display-sm font-bold">
      <Trans>Remove from group</Trans>
    </h1>
  );
  // Until the group and who you are have arrived it isn't known whether you lead it, and so
  // whether its people (and its other leaders) are to be waited for.
  if (groups.isPending || me.isPending || (leads && people.isPending)) {
    return (
      <>
        {toolbar}
        {heading}
        <GroupLoading />
      </>
    );
  }
  if (groups.isError || (leads && people.isError)) {
    return (
      <>
        {toolbar}
        {heading}
        <div className="pt-content-md">
          <GroupLoadFailed
            onRetry={() => void (groups.isError ? groups.refetch() : people.refetch())}
          />
        </div>
      </>
    );
  }
  // A member who doesn't lead can't list the group: they are leaving it, as a Member.
  const person =
    people.data?.find((candidate) => candidate.id === personId) ??
    (!leads && yours
      ? { id: personId, name: '', role: 'member' as const, joinedAt: '', you: true }
      : undefined);
  // Left, removed (on its way out) or an unknown id from a link.
  if (!person) return toolbar;

  const name = person.you ? (me.data?.name ?? me.data?.firstName ?? '') : person.name;
  const [firstName = name] = name.split(/\s+/);
  // The group must keep someone leading it.
  const blocked = leadsGroup(person.role) && !hasOtherLeader(people.data ?? [], person.id);
  const remove = () => {
    if (blocked || removing.isPending) return;
    removing.mutate(person.id, {
      onSuccess: () => {
        // Out of the group: it is no longer one of yours.
        if (person.you) return void navigate({ to: '/account/groups', replace: true });
        void navigate({ to: '/account/groups/$id/people', params: { id }, replace: true });
      },
    });
  };
  return (
    <>
      {toolbar}
      {heading}
      <div className="flex flex-col gap-section-sm pt-content-md">
        <section className="flex flex-col gap-content-sm rounded-xl border border-border-weak bg-surface p-content-sm">
          <div className="flex items-center gap-internal-lg">
            {/* The name follows in text; the photo or initials would only repeat it. */}
            <span aria-hidden className="inline-flex">
              <Avatar
                size={48}
                src={person.you ? photo : undefined}
                initials={initialsOf(name)}
                tone="olive"
                label={name}
              />
            </span>
            <div className="flex min-w-0 flex-1 flex-col items-start gap-internal-sm">
              <span className="w-full truncate text-label-md">{name}</span>
              <GroupRoleTag role={person.role} />
            </div>
          </div>
          <p className="text-body-md text-on-surface-medium">
            {person.you ? (
              <Trans>
                You will no longer be a member of this group, but can request to join again in the
                future.
              </Trans>
            ) : (
              <Trans>
                {firstName} will no longer be a member of this group, but can request to join again
                in the future.
              </Trans>
            )}
          </p>
        </section>
        {blocked ? (
          <div className="flex gap-internal-md">
            <InfoIcon />
            <p className="flex-1 text-body-md text-on-surface-medium">
              <Trans>You need to assign someone else as Leader before you can leave.</Trans>
            </p>
          </div>
        ) : null}
        {removing.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="ghost" onClick={back}>
            {t`Cancel`}
          </Button>
          <Button onClick={remove} disabled={blocked} loading={removing.isPending}>
            {t`Remove from group`}
          </Button>
        </div>
      </div>
    </>
  );
}
